import { computed, getCurrentScope, onScopeDispose, shallowRef, type Ref } from 'vue';
import type { Product, ProductSearch, ProductSearchEvents, ProductSearchSnapshot } from '@/entities/product';
import type { SearchCriteria } from '@/entities/searchCriteria';
import type { FiltersModel, SearchCriteriaFilters, SerpResultsDictionaries } from './types';
import { SERP_FILTERS_RESET_TEXTS } from '../config/texts';
import { filterProducts } from '../lib/filterProducts';
import { createEmptyFiltersModel, isFilterModelSelected } from '../lib/filtersModel';
import { formatFiltersResetDescription, getFiltersResetLabels } from '../lib/filtersReset';
import { isSearchCriteriaFiltersSelected } from '../lib/searchFilters';
import { addToSerpResultsDictionaries } from '../lib/serpResultsDictionaries';
import { toSerpProducts } from '../lib/toSerpProducts';

interface FiltersResetMessage {
  title?: string;
  description: string;
}

interface FiltersResetState {
  message: FiltersResetMessage;
  filtersModel: FiltersModel;
  searchCriteriaFilters: SearchCriteriaFilters | null;
}

export interface FiltersReset {
  message: Readonly<Ref<FiltersResetMessage | null>>;
  resetFiltersForNewCountry(criteria: SearchCriteria): SearchCriteria;
  restore(): SearchCriteriaFilters | null;
  dismiss(): void;
}

export interface FiltersResetOptions {
  userSearchUuid: Readonly<Ref<string | null>>;
  filtersModel: Ref<FiltersModel>;
  replace: (filtersModel: FiltersModel) => void;
  dictionaries: Ref<SerpResultsDictionaries>;
}

const NEW_COUNTRY_MESSAGE: FiltersResetMessage = { description: SERP_FILTERS_RESET_TEXTS.newCountryDescription };

export function useFiltersReset(
  productSearch: ProductSearch,
  { userSearchUuid, filtersModel, replace, dictionaries }: FiltersResetOptions,
): FiltersReset {
  const state = shallowRef<FiltersResetState | null>(null);

  let nextSearchState: FiltersResetState | null = null;

  const dismiss = (): void => {
    state.value = null;
  };

  const restore = (): SearchCriteriaFilters | null => {
    const restoredState = state.value;

    dismiss();
    if (!restoredState) return null;

    replace(restoredState.filtersModel);

    return restoredState.searchCriteriaFilters;
  };

  const resetFiltersForNewCountry = (criteria: SearchCriteria): SearchCriteria => {
    const { meals, hotelCategories, directFlight, block } = criteria;
    const searchCriteriaFilters: SearchCriteriaFilters = { meals, hotelCategories, directFlight, block };

    if (!isFilterModelSelected(filtersModel.value) && !isSearchCriteriaFiltersSelected(searchCriteriaFilters)) {
      return criteria;
    }

    nextSearchState = { message: NEW_COUNTRY_MESSAGE, filtersModel: filtersModel.value, searchCriteriaFilters };
    replace(createEmptyFiltersModel());

    return { ...criteria, meals: [], hotelCategories: [], directFlight: undefined, block: undefined };
  };

  const onSearchStart = ({ searchUuid }: ProductSearchEvents['start']): void => {
    if (searchUuid !== userSearchUuid.value) return;

    state.value = nextSearchState;
    nextSearchState = null;
  };

  const resetFilters = ({ hotels, productsByHotel }: ProductSearchSnapshot<Product>): void => {
    const products = toSerpProducts(hotels, productsByHotel);
    if (!isFilterModelSelected(filtersModel.value) || filterProducts(products, filtersModel.value).length) return;

    const snapshotDictionaries = addToSerpResultsDictionaries(
      dictionaries.value,
      products.flatMap((serpProduct) => serpProduct.products),
    );
    const resetLabels = getFiltersResetLabels(filtersModel.value, snapshotDictionaries);

    state.value = {
      message: { title: SERP_FILTERS_RESET_TEXTS.title, description: formatFiltersResetDescription(resetLabels) },
      filtersModel: filtersModel.value,
      searchCriteriaFilters: null,
    };
    replace(createEmptyFiltersModel());
  };

  const unsubscribes = [productSearch.on('start', onSearchStart), productSearch.on('cache', resetFilters)];

  if (getCurrentScope()) {
    onScopeDispose(() => unsubscribes.forEach((unsubscribe) => unsubscribe()));
  }

  return {
    message: computed(() => state.value?.message ?? null),
    resetFiltersForNewCountry,
    restore,
    dismiss,
  };
}
